/**
 * @module services/zotero/collectionService
 * @description Collection CRUD operations for Zotero API
 */

import { apiClient } from './apiClient';
import type { Collection, LibraryType, ServiceResult } from '@types';
import { buildLibraryEndpoint } from '@types';

// =============================================================================
// COLLECTION SERVICE
// =============================================================================

/**
 * Get collections for a library
 */
export async function getCollections(
  libraryId: number,
  libraryType: LibraryType,
  options: { signal?: AbortSignal } = {}
): Promise<ServiceResult<Collection[]>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    const response = await apiClient.request<Collection[]>({
      method: 'GET',
      path: `${endpoint}/collections`,
      params: {
        limit: 100,
      },
      signal: options.signal,
    });

    return { success: true, data: response.data || [] };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get collections',
    };
  }
}

/**
 * Get a single collection
 */
export async function getCollection(
  libraryId: number,
  libraryType: LibraryType,
  collectionKey: string
): Promise<ServiceResult<Collection>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    const response = await apiClient.request<Collection>({
      method: 'GET',
      path: `${endpoint}/collections/${collectionKey}`,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get collection',
    };
  }
}

/**
 * Create a collection
 */
export async function createCollection(
  libraryId: number,
  libraryType: LibraryType,
  name: string,
  parentCollection?: string
): Promise<ServiceResult<Collection>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    const response = await apiClient.request<{ successful: Record<string, Collection> }>({
      method: 'POST',
      path: `${endpoint}/collections`,
      body: [
        {
          name,
          parentCollection: parentCollection || false,
        },
      ],
    });

    const created = Object.values(response.data?.successful || {})[0];
    if (!created) {
      return { success: false, error: 'Collection creation failed' };
    }

    return { success: true, data: created };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create collection',
    };
  }
}

/**
 * Update a collection
 */
export async function updateCollection(
  libraryId: number,
  libraryType: LibraryType,
  collectionKey: string,
  data: Partial<{ name: string; parentCollection: string | false }>
): Promise<ServiceResult<Collection>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    // Get current version
    const current = await getCollection(libraryId, libraryType, collectionKey);
    if (!current.success) {
      return current;
    }

    const response = await apiClient.request<Collection>({
      method: 'PATCH',
      path: `${endpoint}/collections/${collectionKey}`,
      headers: {
        'If-Unmodified-Since-Version': String(current.data.version),
      },
      body: data,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update collection',
    };
  }
}

/**
 * Delete a collection
 */
export async function deleteCollection(
  libraryId: number,
  libraryType: LibraryType,
  collectionKey: string
): Promise<ServiceResult<void>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    // Get current version
    const current = await getCollection(libraryId, libraryType, collectionKey);
    if (!current.success) {
      return { success: false, error: current.error };
    }

    await apiClient.request<void>({
      method: 'DELETE',
      path: `${endpoint}/collections/${collectionKey}`,
      headers: {
        'If-Unmodified-Since-Version': String(current.data.version),
      },
    });

    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete collection',
    };
  }
}
